import { useQuery } from "@tanstack/react-query";
import { Building2, KeyRound, MapPin, ScrollText, Store, UserCircle2, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

import { usePartnerScope } from "@/components/partner-scope-context";
import { usePageTitle } from "@/lib/use-page-title";
import { apiClient } from "@/lib/api";

/**
 * Landing page - plain-English welcome, at-a-glance counts across
 * the admin's scope, and a recent-activity feed so the operator
 * immediately sees what's happening in their environment.
 */
export function Dashboard(): JSX.Element {
  usePageTitle("Dashboard");
  const { scope } = usePartnerScope();

  const profile = useQuery({
    queryKey: ["profile"],
    queryFn: () => apiClient.getProfile(),
    retry: false,
  });
  // Use the exact same queryKey shape the list pages use so the
  // Dashboard's fetch is a cache hit the moment the user lands on a
  // list page - and so the list pages share the Dashboard's cache
  // on return. Key and queryFn must agree.
  const partners = useQuery({
    queryKey: ["partners", scope.id, { showDeleted: false }],
    queryFn: () => apiClient.listPartners({ include_deleted: false }),
    retry: false,
  });
  const orgs = useQuery({
    queryKey: ["organizations", scope.id, { showDeleted: false, partnerFilter: "" }],
    queryFn: () => apiClient.listOrganizations({ include_deleted: false }),
    retry: false,
  });
  const locs = useQuery({
    queryKey: ["locations", scope.id, { showDeleted: false, statusFilter: "", orgFilter: "" }],
    queryFn: () => apiClient.listLocations({ include_deleted: false }),
    retry: false,
  });
  const users = useQuery({
    queryKey: ["users", scope.id],
    queryFn: () => apiClient.listUsers(),
    retry: false,
  });
  const audit = useQuery({
    queryKey: ["audit", scope.id, { limit: 8 }],
    queryFn: () => apiClient.listAudit({ limit: 8 }),
    retry: false,
  });
  const creds = useQuery({
    queryKey: ["provider-credentials", scope.id, {}],
    queryFn: () => apiClient.listProviderCredentials(),
    retry: false,
  });

  const partnerCount = (partners.data?.partners ?? []).filter(
    (t) => !t.is_deleted && t.partner_type === "partner",
  ).length;
  const orgCount = (orgs.data?.organizations ?? []).filter((o) => !o.is_deleted).length;
  const activeLocations = (locs.data?.locations ?? []).filter(
    (l) => !l.is_deleted && l.status === "active",
  ).length;
  const inactiveLocations = (locs.data?.locations ?? []).filter(
    (l) => !l.is_deleted && l.status === "inactive",
  ).length;
  const userCount = (users.data?.users ?? []).filter((u) => !u.is_disabled).length;
  const credCount = (creds.data?.credentials ?? []).filter((c) => !c.is_deleted).length;

  // Prefer a real first name. If the user hasn't set one yet, fall
  // back to the email's local-part, title-cased on the first dot
  // token (e.g. "ramakrishnan.jayaraman@x" → "Ramakrishnan") -
  // still friendlier than the raw email or a bare "Welcome back.".
  // The Profile page is where the user can set an actual name;
  // link to it from the "You" card.
  const fullName = [profile.data?.first_name, profile.data?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  const greetingName = greetingFrom(profile.data?.first_name, profile.data?.email);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
          Welcome back{greetingName ? `, ${greetingName}` : ""}.
        </h1>
        <p className="text-sm text-muted-foreground">
          Viewing <span className="font-medium text-foreground">{scope.name}</span>.
        </p>
      </div>

      {/* KPI cards - at-a-glance counts across the caller's scope. */}
      <section aria-label="Summary">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <KpiCard
            icon={Building2}
            label="Partners"
            value={partnerCount}
            href="/partners"
            loading={partners.isLoading}
          />
          <KpiCard
            icon={Store}
            label="Organizations"
            value={orgCount}
            href="/organizations"
            loading={orgs.isLoading}
          />
          <KpiCard
            icon={MapPin}
            label="Active locations"
            value={activeLocations}
            subtext={inactiveLocations ? `${inactiveLocations} inactive` : undefined}
            href="/locations"
            loading={locs.isLoading}
          />
          <KpiCard
            icon={Users}
            label="Users"
            value={userCount}
            href="/users"
            loading={users.isLoading}
          />
          <KpiCard
            icon={KeyRound}
            label="Credentials"
            value={credCount}
            href="/provider-credentials"
            loading={creds.isLoading}
          />
          <KpiCard
            icon={ScrollText}
            label="Recent events"
            value={audit.data?.items.length ?? 0}
            subtext="last 8"
            href="/audit"
            loading={audit.isLoading}
          />
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Recent activity */}
        <section
          aria-label="Recent activity"
          className="rounded-lg border bg-card p-4 shadow-sm sm:p-5 lg:col-span-2"
        >
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold">Recent activity</h2>
            <Link to="/audit" className="text-xs text-muted-foreground hover:text-foreground">
              View all →
            </Link>
          </div>
          {audit.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {audit.isError && (
            <p className="text-sm text-destructive">Couldn't load recent activity.</p>
          )}
          {audit.data && audit.data.items.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Nothing's happened here yet. Activity will appear as you create partners,
              organizations, locations, and users.
            </p>
          )}
          {audit.data && audit.data.items.length > 0 && (
            <ul className="divide-y text-sm">
              {audit.data.items.map((e) => (
                <li key={e.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                  <span className="font-mono text-xs font-medium text-primary">{e.action}</span>
                  <span className="text-xs text-muted-foreground">
                    by {e.actor.email ?? e.actor.user_id ?? "-"}
                  </span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {formatRelative(new Date(e.at))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* You */}
        <section
          aria-label="Your account"
          className="rounded-lg border bg-card p-4 shadow-sm sm:p-5"
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserCircle2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <h2 className="text-sm font-semibold">You</h2>
            </div>
            <Link to="/profile" className="text-xs text-muted-foreground hover:text-foreground">
              Edit →
            </Link>
          </div>
          {profile.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {profile.data && (
            <div className="space-y-1 text-sm">
              <p className="break-words font-medium">{fullName || profile.data.email}</p>
              {fullName && (
                <p className="break-words text-xs text-muted-foreground">{profile.data.email}</p>
              )}
              <p className="pt-1 text-xs text-muted-foreground">
                {profile.data.roles.length === 0
                  ? "No roles granted."
                  : `${profile.data.roles.length} role${profile.data.roles.length === 1 ? "" : "s"} across your partners.`}
              </p>
              {!fullName && (
                <Link
                  to="/profile"
                  className="inline-block pt-2 text-xs text-primary hover:underline"
                >
                  Set your name →
                </Link>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function KpiCard(props: {
  icon: LucideIcon;
  label: string;
  value: number;
  subtext?: string | undefined;
  href: string;
  loading?: boolean | undefined;
}): JSX.Element {
  const Icon = props.icon;
  return (
    <Link
      to={props.href}
      className="flex flex-col gap-1.5 rounded-lg border bg-card p-4 shadow-sm transition hover:border-primary/40 hover:shadow"
    >
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {props.label}
      </div>
      <div className="text-2xl font-semibold tabular-nums">
        {props.loading ? <span className="text-muted-foreground">-</span> : props.value}
      </div>
      {props.subtext && <div className="text-[11px] text-muted-foreground">{props.subtext}</div>}
    </Link>
  );
}

/**
 * Produce a friendly greeting name, in order of preference:
 *   1. The user's real first_name if set
 *   2. The first dot-token of the email's local-part, title-cased
 *   3. Empty - caller renders "Welcome back." without a name
 *
 * E.g. first_name="Jane" → "Jane"; email="ramakrishnan.j@x.com" →
 * "Ramakrishnan"; email="a@x.com" → "A".
 */
function greetingFrom(firstName: string | null | undefined, email: string | undefined): string {
  const trimmed = firstName?.trim();
  if (trimmed) return trimmed;
  if (!email) return "";
  const local = email.split("@")[0] ?? "";
  const first = local.split(".")[0] ?? "";
  if (!first) return "";
  return first.charAt(0).toUpperCase() + first.slice(1);
}

/** Human-readable relative time - "3m ago", "2h ago", "yesterday", etc. */
function formatRelative(then: Date): string {
  const diff = Date.now() - then.getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return "yesterday";
  if (d < 7) return `${d}d ago`;
  return then.toLocaleDateString();
}
